import { AuthProvider } from "@bw/shared/components";
import { BwMaterialProvider } from "@bw/material";
import { type ReactNode } from "react";
import { BusinessErrorBoundary } from "./BusinessErrorBoundary";
import { BusinessNotificationProvider } from "./notifications";
import { createBusinessTheme } from "./theme";

const THEME_STORAGE_KEY = "bw-business-ui-theme";
const businessTheme = createBusinessTheme();
const COLOR_MODES = new Set(["light", "dark", "system"]);

type StorageHandler = (value: unknown) => void;

function isColorMode(value: unknown): value is "light" | "dark" | "system" {
  return typeof value === "string" && COLOR_MODES.has(value);
}

/**
 * MUI's color-scheme manager keeps the established Business mode key intact.
 * The separate color-scheme payload cannot share that scalar key, so it gets a
 * namespaced companion while `mode` continues to read/write the established value.
 */
function businessColorSchemeStorageManager({
  key,
  storageWindow,
}: {
  key: string;
  storageWindow?: Window | null;
}) {
  const targetWindow =
    storageWindow ?? (typeof window === "undefined" ? null : window);
  const storageKey =
    key === "mode" ? THEME_STORAGE_KEY : `${THEME_STORAGE_KEY}-${key}`;

  return {
    get(defaultValue: unknown) {
      if (!targetWindow) return defaultValue;
      try {
        const storedValue = targetWindow.localStorage.getItem(storageKey);
        if (key === "mode") {
          return isColorMode(storedValue) ? storedValue : defaultValue;
        }
        return storedValue ?? defaultValue;
      } catch {
        return defaultValue;
      }
    },
    set(value: unknown) {
      if (!targetWindow) return;
      if (key === "mode" && !isColorMode(value)) return;
      try {
        targetWindow.localStorage.setItem(storageKey, String(value));
      } catch {
        // Color mode remains usable in memory when storage is unavailable.
      }
    },
    subscribe(handler: StorageHandler) {
      if (!targetWindow) return () => undefined;
      const onStorage = (event: StorageEvent) => {
        if (event.key !== storageKey) return;
        if (key === "mode") {
          handler(isColorMode(event.newValue) ? event.newValue : null);
          return;
        }
        handler(event.newValue);
      };
      targetWindow.addEventListener("storage", onStorage);
      return () => targetWindow.removeEventListener("storage", onStorage);
    },
  };
}

/**
 * App-wide providers: Material theme + auth + native MUI notifications. The React Query client
 * lives INSIDE <Router/> (app/router.tsx), so the router remains a self-contained
 * data unit. MUI is the only theme preference owner.
 */
export function Providers({ children }: { children: ReactNode }) {
  return (
    <BwMaterialProvider
      theme={businessTheme}
      defaultMode="system"
      storageManager={businessColorSchemeStorageManager}
    >
      <BusinessErrorBoundary>
        <AuthProvider>
          <BusinessNotificationProvider>{children}</BusinessNotificationProvider>
        </AuthProvider>
      </BusinessErrorBoundary>
    </BwMaterialProvider>
  );
}
